"use client";

import { useEffect, useRef, useState } from "react";

type Locale = "es" | "en" | "de";

const MONTHS: Record<Locale, string[]> = {
  es: ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"],
  en: ["January","February","March","April","May","June","July","August","September","October","November","December"],
  de: ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"],
};

const DAYS: Record<Locale, string[]> = {
  es: ["Lu","Ma","Mi","Ju","Vi","Sá","Do"],
  en: ["Mo","Tu","We","Th","Fr","Sa","Su"],
  de: ["Mo","Di","Mi","Do","Fr","Sa","So"],
};

type Props = {
  value: string;           // YYYY-MM-DD or ""
  onChange: (v: string) => void;
  required?: boolean;
  className?: string;      // default "app-input"
  compact?: boolean;       // smaller trigger for nav controls
  locale?: Locale;
  placeholder?: string;
  ariaLabel?: string;
  min?: string;            // YYYY-MM-DD
  max?: string;
};

function isoToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

function formatDisplay(v: string, locale: Locale): string {
  if (!v) return "";
  const [y, m, d] = v.split("-");
  if (locale === "en") return `${m}/${d}/${y}`;
  return `${d}/${m}/${y}`;
}

export function DateInput({
  value, onChange, required, className = "app-input",
  compact = false, locale = "es", placeholder, ariaLabel, min, max,
}: Props) {
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(() => value ? parseInt(value.slice(0,4)) : new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(() => value ? parseInt(value.slice(5,7))-1 : new Date().getMonth());
  const wrapRef = useRef<HTMLDivElement>(null);
  const today = isoToday();

  // Sync view when value changes externally
  useEffect(() => {
    if (!value) return;
    const timer = window.setTimeout(() => {
      setViewYear(parseInt(value.slice(0,4)));
      setViewMonth(parseInt(value.slice(5,7))-1);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [value]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) { if (e.key === "Escape") setOpen(false); }
    function onOut(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onOut);
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onOut); };
  }, [open]);

  function prevMonth() {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y-1); }
    else setViewMonth(m => m-1);
  }
  function nextMonth() {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y+1); }
    else setViewMonth(m => m+1);
  }

  function selectDay(day: number) {
    const m = String(viewMonth+1).padStart(2,"0");
    const d = String(day).padStart(2,"0");
    const iso = `${viewYear}-${m}-${d}`;
    if (min && iso < min) return;
    if (max && iso > max) return;
    onChange(iso);
    setOpen(false);
  }

  // Build calendar grid (Mon-first)
  const daysInMonth = new Date(viewYear, viewMonth+1, 0).getDate();
  const firstDow = (new Date(viewYear, viewMonth, 1).getDay() + 6) % 7; // 0=Mon
  const cells: (number|null)[] = [...Array(firstDow).fill(null), ...Array.from({length:daysInMonth},(_,i)=>i+1)];
  while (cells.length % 7) cells.push(null);

  const displayValue = formatDisplay(value, locale);
  const triggerClass = compact ? `${className} date-input-trigger date-input-trigger--compact` : `${className} date-input-trigger`;

  return (
    <div ref={wrapRef} className={compact ? "date-input-wrap date-input-wrap--compact" : "date-input-wrap"}>
      <button aria-label={ariaLabel ?? (value ? `Fecha seleccionada: ${displayValue}` : "Seleccionar fecha")} type="button" className={triggerClass} onClick={() => setOpen(o => !o)} aria-haspopup="dialog" aria-expanded={open}>
        <span className={displayValue ? "date-input-val" : "date-input-ph"}>
          {displayValue || (placeholder ?? (locale === "en" ? "mm/dd/yyyy" : "dd/mm/aaaa"))}
        </span>
        <svg className="date-input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
      </button>

      {open && (
        <div className="dpop" role="dialog" aria-modal="true">
          <div className="dpop-head">
            <button type="button" className="dpop-nav" onClick={prevMonth} aria-label="Mes anterior">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            <span className="dpop-month">{MONTHS[locale][viewMonth]} {viewYear}</span>
            <button type="button" className="dpop-nav" onClick={nextMonth} aria-label="Mes siguiente">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          </div>

          <div className="dpop-grid">
            {DAYS[locale].map(d => <div key={d} className="dpop-dn">{d}</div>)}
            {cells.map((day, i) => {
              if (!day) return <div key={`_${i}`}/>;
              const iso = `${viewYear}-${String(viewMonth+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
              const isSel = iso === value;
              const isToday = iso === today;
              const disabled = (min && iso < min) || (max && iso > max);
              return (
                <button
                  key={day}
                  type="button"
                  className={["dpop-day", isSel && "is-sel", isToday && "is-today", disabled && "is-off"].filter(Boolean).join(" ")}
                  onClick={() => !disabled && selectDay(day)}
                  aria-current={isToday ? "date" : undefined}
                  aria-label={`${day} de ${MONTHS[locale][viewMonth]} de ${viewYear}`}
                  tabIndex={disabled ? -1 : 0}
                >
                  {day}
                </button>
              );
            })}
          </div>

          {value && (
            <button type="button" className="dpop-clear" onClick={() => { onChange(""); setOpen(false); }}>
              {locale === "en" ? "Clear" : locale === "de" ? "Löschen" : "Borrar fecha"}
            </button>
          )}
        </div>
      )}

      {/* Hidden input so HTML form validation / name submission works */}
      <input type="hidden" name={undefined} value={value} required={required} />
    </div>
  );
}
